import { FormatPill } from '../FormatPill';
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { 
  CheckCircle2, 
  Clock, 
  XCircle, 
  Send, 
  Building2,
  Users,
  Trophy,
  ChevronRight,
  MessageSquare,
  Calculator,
  ArrowRight,
  Check,
  X,
  Loader2,
  Edit2
} from 'lucide-react';

/**
 * ValidationWorkflow - Visual workflow component showing the validation steps
 * Redesigned for clarity:
 * - ÉTAPE APPLICATION: Proposition automatique du système
 * - ÉTAPE COMMERCE: Proposition du commercial  
 * - ÉTAPE DÉCISION: Acceptation ou contre-proposition de la Direction
 */
export const ValidationWorkflow = ({ 
  simulation, 
  language = 'fr', 
  onRespondCounterProposal, 
  isCommercial = false,
  onModifyCommercial,
  onModifyDirection,
  onValidateDirection,
  analysisSource = 'tool',
  previewData = null,
  // Dynamic values that update when hypotheses are toggled
  dynamicCaHtAnnual = null,
  dynamicCommissionRate = null,
  dynamicRedMonths = null
}) => {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseComment, setResponseComment] = useState('');
  const [responding, setResponding] = useState(false);
  
  // Inline editing states for Commercial
  const [editingCommercial, setEditingCommercial] = useState(false);
  const [editCommercialCA, setEditCommercialCA] = useState('');
  const [editCommercialCommission, setEditCommercialCommission] = useState('');
  const [savingCommercial, setSavingCommercial] = useState(false);
  
  // Inline editing states for Direction
  const [editingDirection, setEditingDirection] = useState(false);
  const [editDirectionCA, setEditDirectionCA] = useState('');
  const [editDirectionCommission, setEditDirectionCommission] = useState('');
  const [savingDirection, setSavingDirection] = useState(false);
  
  if (!simulation) return null;

  const t = {
    fr: {
      stepApplication: "ÉTAPE APPLICATION",
      stepCommerce: "ÉTAPE COMMERCE", 
      stepDecision: "ÉTAPE DÉCISION",
      systemProposal: "Proposition du Système",
      commercialProposal: "Proposition Commerciale",
      directionDecision: "Décision Direction",
      autoCalculation: "Calcul automatique basé sur les données saisies",
      waitingValidation: "En attente de validation",
      waitingDecision: "En attente de décision",
      proposalSent: "Proposition envoyée",
      notSubmitted: "Non soumise",
      commission: "Commission",
      caPrevu: "CA Prévisionnel HT",
      verdict: "Verdict",
      approved: "GO - Validé",
      rejected: "NO GO - Refusé",
      counterProposal: "Contre-proposition",
      underReview: "À l'étude",
      acceptedProposal: "Proposition acceptée",
      comment: "Commentaire",
      finalStatus: "Statut Final",
      won: "Dossier Gagné",
      lost: "Dossier Perdu",
      ongoing: "En cours",
      validateAndSend: "Valider et envoyer",
      orNewProposal: "ou soumettre nouvelle proposition",
      acceptCounterProposal: "Accepter la contre-proposition",
      refuseCounterProposal: "Refuser la contre-proposition",
      responseRequired: "Votre réponse est requise",
      addComment: "Ajouter un commentaire (optionnel)",
      confirm: "Confirmer"
    },
    en: {
      stepApplication: "APPLICATION STEP",
      stepCommerce: "COMMERCE STEP",
      stepDecision: "DECISION STEP",
      systemProposal: "System Proposal",
      commercialProposal: "Commercial Proposal",
      directionDecision: "Management Decision",
      autoCalculation: "Automatic calculation based on entered data",
      waitingValidation: "Waiting for validation",
      waitingDecision: "Waiting for decision",
      proposalSent: "Proposal sent",
      notSubmitted: "Not submitted",
      commission: "Commission",
      caPrevu: "Projected Revenue",
      verdict: "Verdict",
      approved: "GO - Approved",
      rejected: "NO GO - Rejected",
      counterProposal: "Counter-proposal",
      underReview: "Under Review",
      acceptedProposal: "Accepted proposal",
      comment: "Comment",
      finalStatus: "Final Status",
      won: "Won",
      lost: "Lost",
      ongoing: "Ongoing",
      validateAndSend: "Validate and send",
      orNewProposal: "or submit new proposal",
      acceptCounterProposal: "Accept counter-proposal",
      refuseCounterProposal: "Refuse counter-proposal",
      responseRequired: "Your response is required",
      addComment: "Add a comment (optional)",
      confirm: "Confirm"
    }
  }[language];

  // Determine step statuses
  const hasToolCalculation = simulation.verdict !== undefined;
  const hasCommercialProposal = simulation.validated === true;
  const hasAdminDecision = simulation.admin_status !== undefined && simulation.admin_status !== null;
  const hasCounterProposal = simulation.admin_status === 'counter_proposal' || simulation.admin_counter_proposal;
  const hasBusinessStatus = simulation.business_status !== undefined && simulation.business_status !== null;

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const getVerdictBadge = (verdict) => {
    const config = {
      'GO': { className: 'bg-emerald-500 text-white', label: 'GO' },
      'A_ETUDE': { className: 'bg-amber-500 text-white', label: "À L'ÉTUDE" },
      'NO_GO': { className: 'bg-red-500 text-white', label: 'NO GO' }
    };
    const c = config[verdict] || config['GO'];
    return <Badge className={`${c.className} text-xs font-bold`}>{c.label}</Badge>;
  };

  // ========== STEP 1: APPLICATION ==========
  const renderStepApplication = () => {
    const isActive = hasToolCalculation;
    // Use dynamic values if provided (updated when hypotheses are toggled), otherwise fallback to stored values
    const ca = dynamicCaHtAnnual ?? simulation.average_hypothesis?.ca_ht_annual;
    const commission = dynamicCommissionRate ?? simulation.commission_rate_used ?? simulation.input_data?.hypothesis_params?.commission_rate;
    
    // Porteur CAPEX = choix saisi (corner/vitrine/inox), pour TOUTES les enseignes
    // y compris Système U. La grille de commission reste inchangée (gérée à part).
    const capex = simulation.input_data?.capex_investment || {};
    const clientCarriesAll = (
      capex.corner_investor === 'CLIENT' && 
      capex.vitrine_investor === 'CLIENT' && 
      capex.inox_investor === 'CLIENT'
    );
    const capexCarrier = clientCarriesAll ? 'CLIENT' : 'CCD';
    
    // Contribution Margin (red months) - Use dynamic value if provided, otherwise fallback
    const redMonths = dynamicRedMonths ?? simulation.red_months_count ?? 0;
    const cmColor = redMonths < 3 ? 'emerald' : redMonths <= 6 ? 'amber' : 'red';
    const cmBgClass = redMonths < 3 ? 'bg-emerald-100 text-emerald-800' : 
                      redMonths <= 6 ? 'bg-amber-100 text-amber-800' : 
                      'bg-red-100 text-red-800';
    
    return (
      <div className={`flex-1 min-w-0 ${!isActive ? 'opacity-50' : ''}`}>
        <div className="mb-2 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Calculator className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs font-bold text-blue-600 dark:text-blue-400 tracking-wide">
            {t.stepApplication}
          </span>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-900/20 dark:to-slate-800 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-800 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
              {t.systemProposal}
              <FormatPill format={simulation.input_data?.general_info?.format} testid="wf-format-system" />
            </h4>
            {isActive && (
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            )}
          </div>
          
          {isActive ? (
            <>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                {t.autoCalculation}
              </p>
              
              {/* Values Box - Condensed */}
              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 space-y-2 border border-blue-100 dark:border-blue-900">
                {/* Porteur CAPEX avec montant total */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{language === 'fr' ? 'Porteur CAPEX:' : 'CAPEX Carrier:'}</span>
                  <Badge className={`text-xs font-bold ${clientCarriesAll ? 'bg-emerald-500 text-white' : 'bg-violet-500 text-white'}`}>
                    {capexCarrier}
                  </Badge>
                </div>
                {/* CAPEX Total - sous Porteur CAPEX */}
                <div className="flex justify-between items-center pl-2 text-[11px]">
                  <span className="text-slate-400">{language === 'fr' ? 'CAPEX CCD :' : 'CCD CAPEX:'}</span>
                  <span className="font-semibold text-slate-600 dark:text-slate-300">
                    {formatCurrency(simulation.ccd_capex ?? simulation.total_capex ?? 0)}
                  </span>
                </div>
                {/* CA Prévisionnel */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{t.caPrevu}:</span>
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(ca)}
                  </span>
                </div>
                {/* Commission */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{t.commission}:</span>
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {((commission || 0) * 100).toFixed(0)}%
                  </span>
                </div>
                {/* Contribution Margin - Mois avec couleur */}
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-700">
                  <span className="text-xs text-slate-500">{language === 'fr' ? 'Contribution Margin:' : 'Contribution Margin:'}</span>
                  <Badge className={`text-xs font-bold ${cmBgClass}`}>
                    {redMonths} {language === 'fr' ? 'mois' : 'months'}
                  </Badge>
                </div>
              </div>
              
              {/* Action hint if no commercial proposal yet */}
              {!hasCommercialProposal && (
                <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                    <Send className="w-3 h-3" />
                    {t.validateAndSend} {t.orNewProposal}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span className="text-xs">{t.waitingValidation}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== STEP 2: COMMERCE ==========
  const renderStepCommerce = () => {
    const isActive = hasCommercialProposal;
    const isPending = hasToolCalculation && !hasCommercialProposal;
    
    // Porteur CAPEX = choix saisi (corner/vitrine/inox), pour TOUTES les enseignes
    // y compris Système U. La grille de commission reste inchangée (gérée à part).
    const capex = simulation.input_data?.capex_investment || {};
    const clientCarriesAll = (
      capex.corner_investor === 'CLIENT' && 
      capex.vitrine_investor === 'CLIENT' && 
      capex.inox_investor === 'CLIENT'
    );
    const capexCarrier = clientCarriesAll ? 'CLIENT' : 'CCD';
    
    // Use commercial-specific red months count (stored by backend when commercial modifies)
    // This value is calculated by the backend based on commercial's CA and commission
    const redMonths = simulation.commercial_red_months_count ?? simulation.red_months_count ?? 0;
    const cmBgClass = redMonths < 3 ? 'bg-emerald-100 text-emerald-800' : 
                      redMonths <= 6 ? 'bg-amber-100 text-amber-800' : 
                      'bg-red-100 text-red-800';
    
    return (
      <div className={`flex-1 min-w-0 ${!isActive && !isPending ? 'opacity-40' : ''}`}>
        <div className="mb-2 flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
            isActive 
              ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
              : isPending 
                ? 'bg-gradient-to-br from-amber-400 to-amber-500'
                : 'bg-slate-300'
          }`}>
            <Users className="w-4 h-4 text-white" />
          </div>
          <span className={`text-xs font-bold tracking-wide ${
            isActive 
              ? 'text-purple-600 dark:text-purple-400' 
              : isPending 
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-slate-400'
          }`}>
            {t.stepCommerce}
          </span>
        </div>
        
        <div className={`rounded-xl p-4 border-2 shadow-sm ${
          isActive 
            ? 'bg-gradient-to-br from-purple-50 to-slate-50 dark:from-purple-900/20 dark:to-slate-800 border-purple-200 dark:border-purple-800' 
            : isPending
              ? 'bg-gradient-to-br from-amber-50 to-slate-50 dark:from-amber-900/20 dark:to-slate-800 border-amber-200 dark:border-amber-700 border-dashed'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
              {t.commercialProposal}
              <FormatPill format={simulation.input_data?.general_info?.format} testid="wf-format-commercial" />
            </h4>
            {isActive ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            ) : isPending ? (
              <Clock className="w-5 h-5 text-amber-500" />
            ) : null}
          </div>
          
          {isActive ? (
            <>
              <div className="flex items-center gap-1 mb-3">
                <Send className="w-3 h-3 text-purple-500" />
                <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                  {t.proposalSent}
                </span>
              </div>
              
              {/* Values Box - Condensed with all info - EDITABLE */}
              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 space-y-2 border border-purple-100 dark:border-purple-900">
                {/* Porteur CAPEX avec montant total */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{language === 'fr' ? 'Porteur CAPEX:' : 'CAPEX Carrier:'}</span>
                  <Badge className={`text-xs font-bold ${clientCarriesAll ? 'bg-emerald-500 text-white' : 'bg-violet-500 text-white'}`}>
                    {capexCarrier}
                  </Badge>
                </div>
                {/* CAPEX Total - sous Porteur CAPEX */}
                <div className="flex justify-between items-center pl-2 text-[11px]">
                  <span className="text-slate-400">{language === 'fr' ? 'CAPEX CCD :' : 'CCD CAPEX:'}</span>
                  <span className="font-semibold text-slate-600 dark:text-slate-300">
                    {formatCurrency(simulation.ccd_capex ?? simulation.total_capex ?? 0)}
                  </span>
                </div>
                {/* CA Prévisionnel - EDITABLE */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{t.caPrevu}:</span>
                  {editingCommercial ? (
                    <input
                      type="number"
                      value={editCommercialCA}
                      onChange={(e) => setEditCommercialCA(e.target.value)}
                      className="w-24 px-2 py-1 text-sm font-bold text-purple-600 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      placeholder="CA HT"
                    />
                  ) : (
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(simulation.ca_previsionnel_ht_proposed)}
                    </span>
                  )}
                </div>
                {/* Commission - EDITABLE */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{t.commission}:</span>
                  {editingCommercial ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={editCommercialCommission}
                        onChange={(e) => setEditCommercialCommission(e.target.value)}
                        className="w-16 px-2 py-1 text-sm font-bold text-purple-600 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 focus:outline-none"
                        placeholder="%"
                        min="0"
                        max="100"
                      />
                      <span className="text-sm text-purple-600">%</span>
                    </div>
                  ) : (
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                      {((simulation.commission_rate_proposed || 0) * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                {/* Contribution Margin - Mois avec couleur */}
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-700">
                  <span className="text-xs text-slate-500">{language === 'fr' ? 'Contribution Margin:' : 'Contribution Margin:'}</span>
                  <Badge className={`text-xs font-bold ${cmBgClass}`}>
                    {redMonths} {language === 'fr' ? 'mois' : 'months'}
                  </Badge>
                </div>
              </div>
              
              {/* Comment if any */}
              {simulation.commercial_comment && (
                <div className="mt-2 flex items-start gap-1">
                  <MessageSquare className="w-3 h-3 text-purple-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2">
                    {simulation.commercial_comment}
                  </p>
                </div>
              )}
              
              {/* Modifier button for commercial - INLINE EDITING */}
              <div className="mt-3 pt-2 border-t border-purple-100 dark:border-purple-800">
                {editingCommercial ? (
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      disabled={savingCommercial}
                      onClick={async () => {
                        if (!onModifyCommercial) {
                          // Fallback to scroll if no handler
                          const element = document.getElementById('proposition-client-section');
                          if (element) element.scrollIntoView({ behavior: 'smooth' });
                          setEditingCommercial(false);
                          return;
                        }
                        setSavingCommercial(true);
                        try {
                          await onModifyCommercial({
                            ca_previsionnel_ht: parseFloat(editCommercialCA),
                            commission_rate: parseFloat(editCommercialCommission) / 100
                          });
                          setEditingCommercial(false);
                        } catch (error) {
                          console.error('Error saving commercial proposal:', error);
                        } finally {
                          setSavingCommercial(false);
                        }
                      }}
                      className="flex-1 gap-1.5 bg-purple-600 hover:bg-purple-700"
                    >
                      {savingCommercial ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                      {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingCommercial(false)}
                      className="gap-1.5 text-slate-600 border-slate-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditCommercialCA(simulation.ca_previsionnel_ht_proposed?.toString() || '');
                      setEditCommercialCommission(((simulation.commission_rate_proposed || 0) * 100).toFixed(0));
                      setEditingCommercial(true);
                    }}
                    className="w-full gap-1.5 text-purple-600 border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                    data-testid="modify-commercial-btn"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'Modifier' : 'Edit'}
                  </Button>
                )}
              </div>
            </>
          ) : isPending ? (
            <div className="text-center py-2">
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {t.waitingValidation}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">
                {t.notSubmitted}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span className="text-xs">{t.notSubmitted}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========== STEP 3: DECISION ==========
  const renderStepDecision = () => {
    const isActive = hasAdminDecision;
    const isPending = hasCommercialProposal && !hasAdminDecision;
    const isApproved = simulation.admin_status === 'approved';
    const isRejected = simulation.admin_status === 'rejected';
    const isUnderReview = simulation.admin_status === 'pending_review';
    const isCounter = simulation.admin_status === 'counter_proposal' || hasCounterProposal;
    
    return (
      <div className={`flex-1 min-w-0 ${!isActive && !isPending ? 'opacity-40' : ''}`}>
        <div className="mb-2 flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
            isApproved 
              ? 'bg-gradient-to-br from-emerald-500 to-emerald-600'
              : isRejected
                ? 'bg-gradient-to-br from-red-500 to-red-600'
                : isUnderReview || isCounter
                  ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                  : isPending
                    ? 'bg-gradient-to-br from-amber-400 to-amber-500'
                    : 'bg-slate-300'
          }`}>
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className={`text-xs font-bold tracking-wide ${
            isApproved 
              ? 'text-emerald-600 dark:text-emerald-400'
              : isRejected
                ? 'text-red-600 dark:text-red-400'
                : isActive || isPending
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-slate-400'
          }`}>
            {t.stepDecision}
          </span>
        </div>
        
        <div className={`rounded-xl p-4 border-2 shadow-sm ${
          isApproved 
            ? 'bg-gradient-to-br from-emerald-50 to-slate-50 dark:from-emerald-900/20 dark:to-slate-800 border-emerald-300 dark:border-emerald-700'
            : isRejected
              ? 'bg-gradient-to-br from-red-50 to-slate-50 dark:from-red-900/20 dark:to-slate-800 border-red-300 dark:border-red-700'
              : isUnderReview || isCounter
                ? 'bg-gradient-to-br from-amber-50 to-slate-50 dark:from-amber-900/20 dark:to-slate-800 border-amber-300 dark:border-amber-700'
                : isPending
                  ? 'bg-gradient-to-br from-amber-50 to-slate-50 dark:from-amber-900/20 dark:to-slate-800 border-amber-200 dark:border-amber-700 border-dashed'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
              {t.directionDecision}
              <FormatPill format={simulation.input_data?.general_info?.format} testid="wf-format-decision" />
            </h4>
            <div className="flex items-center gap-2">
              {/* Badge « Signé » quand le dossier est signé/gagné */}
              {(simulation.business_status === 'signed' || simulation.business_status === 'won') ? (
                <Badge className="bg-emerald-500 text-white text-xs font-bold gap-1" data-testid="dossier-signed-badge">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Signé' : 'Signed'}
                </Badge>
              ) : (isActive && (isApproved || isCounter) && onValidateDirection) ? (
                /* Bouton « Signer » — accessible à tous les profils, pour lancer l'annonce ALL */
                <Button
                  size="sm"
                  onClick={() => onValidateDirection()}
                  className="h-7 px-3 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                  title={language === 'fr' ? "Signer le dossier pour pouvoir faire l'annonce ALL" : 'Sign the dossier to make the ALL announcement'}
                  data-testid="sign-dossier-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Signer' : 'Sign'}
                </Button>
              ) : isApproved ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              ) : isRejected ? (
                <XCircle className="w-5 h-5 text-red-500" />
              ) : isPending ? (
                <Clock className="w-5 h-5 text-amber-500" />
              ) : isUnderReview || isCounter ? (
                <Clock className="w-5 h-5 text-amber-500" />
              ) : null}
            </div>
          </div>
          
          {isActive ? (
            <>
              {/* Status Badge */}
              <div className="mb-3 flex flex-wrap gap-2">
                {isApproved && (
                  <Badge className="bg-emerald-500 text-white text-xs font-bold">
                    {t.approved}
                  </Badge>
                )}
                {isRejected && (
                  <Badge className="bg-red-500 text-white text-xs font-bold">
                    {t.rejected}
                  </Badge>
                )}
                {isUnderReview && (
                  <Badge className="bg-amber-500 text-white text-xs font-bold">
                    {t.underReview}
                  </Badge>
                )}
                {/* Show counter-proposal badge if exists, even when approved */}
                {(isCounter || simulation.admin_counter_proposal?.commission_rate || simulation.admin_counter_proposal?.ca_previsionnel_ht) && (
                  <Badge className="bg-orange-500 text-white text-xs font-bold">
                    {t.counterProposal}
                  </Badge>
                )}
              </div>
              
              {/* Show values for approved, counter-proposal, or under review - EDITABLE */}
              {(isApproved || isCounter || isUnderReview) && (() => {
                // Porteur CAPEX = choix saisi (corner/vitrine/inox), même pour Système U.
                const capex = simulation.input_data?.capex_investment || {};
                const clientCarriesAll = (
                  capex.corner_investor === 'CLIENT' && 
                  capex.vitrine_investor === 'CLIENT' && 
                  capex.inox_investor === 'CLIENT'
                );
                const capexCarrier = clientCarriesAll ? 'CLIENT' : 'CCD';
                
                // Determine if there's a counter-proposal to use
                const hasCounterProposal = simulation.admin_counter_proposal?.commission_rate || simulation.admin_counter_proposal?.ca_previsionnel_ht;
                
                // Use direction-specific red months count (from counter-proposal or approved decision)
                // When the Direction accepts the proposal AS-IS (no counter-proposal), the card
                // displays the commercial's proposed CA & commission, so the Contribution Margin
                // must use the commercial-recomputed red months — NOT the original system value.
                const redMonths = hasCounterProposal 
                  ? (simulation.admin_counter_proposal?.red_months_count ?? simulation.direction_red_months_count ?? simulation.red_months_count ?? 0)
                  : (simulation.commercial_red_months_count ?? simulation.red_months_count ?? 0);
                const cmBgClass = redMonths < 3 ? 'bg-emerald-100 text-emerald-800' : 
                                  redMonths <= 6 ? 'bg-amber-100 text-amber-800' : 
                                  'bg-red-100 text-red-800';
                
                // Get the correct values (counter-proposal takes priority)
                const displayCA = hasCounterProposal 
                  ? simulation.admin_counter_proposal?.ca_previsionnel_ht 
                  : simulation.ca_previsionnel_ht_proposed;
                const displayCommission = hasCounterProposal 
                  ? simulation.admin_counter_proposal?.commission_rate 
                  : simulation.commission_rate_proposed;
                
                // Determine label based on whether there's a counter-proposal
                const proposalLabel = hasCounterProposal ? t.counterProposal : t.acceptedProposal;
                const valueColor = hasCounterProposal 
                  ? 'text-orange-600 dark:text-orange-400' 
                  : 'text-emerald-600 dark:text-emerald-400';
                
                return (
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 space-y-2 border border-slate-100 dark:border-slate-700">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">
                      {proposalLabel}
                    </p>
                    {/* Porteur CAPEX avec montant total */}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">{language === 'fr' ? 'Porteur CAPEX:' : 'CAPEX Carrier:'}</span>
                      <Badge className={`text-xs font-bold ${clientCarriesAll ? 'bg-emerald-500 text-white' : 'bg-violet-500 text-white'}`}>
                        {capexCarrier}
                      </Badge>
                    </div>
                    {/* CAPEX Total - sous Porteur CAPEX */}
                    <div className="flex justify-between items-center pl-2 text-[11px]">
                      <span className="text-slate-400">{language === 'fr' ? 'CAPEX CCD :' : 'CCD CAPEX:'}</span>
                      <span className="font-semibold text-slate-600 dark:text-slate-300">
                        {formatCurrency(simulation.ccd_capex ?? simulation.total_capex ?? 0)}
                      </span>
                    </div>
                    {/* CA Prévisionnel - EDITABLE */}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">{t.caPrevu}:</span>
                      {editingDirection ? (
                        <input
                          type="number"
                          value={editDirectionCA}
                          onChange={(e) => setEditDirectionCA(e.target.value)}
                          className="w-24 px-2 py-1 text-sm font-bold text-emerald-600 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                          placeholder="CA HT"
                        />
                      ) : (
                        <span className={`text-sm font-bold ${valueColor}`}>
                          {formatCurrency(displayCA)}
                        </span>
                      )}
                    </div>
                    {/* Commission - EDITABLE */}
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">{t.commission}:</span>
                      {editingDirection ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={editDirectionCommission}
                            onChange={(e) => setEditDirectionCommission(e.target.value)}
                            className="w-16 px-2 py-1 text-sm font-bold text-emerald-600 border border-emerald-300 rounded focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                            placeholder="%"
                            min="0"
                            max="100"
                          />
                          <span className="text-sm text-emerald-600">%</span>
                        </div>
                      ) : (
                        <span className={`text-sm font-bold ${valueColor}`}>
                          {((displayCommission || 0) * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {/* Contribution Margin - Mois avec couleur */}
                    <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-700">
                      <span className="text-xs text-slate-500">{language === 'fr' ? 'Contribution Margin:' : 'Contribution Margin:'}</span>
                      <Badge className={`text-xs font-bold ${cmBgClass}`}>
                        {redMonths} {language === 'fr' ? 'mois' : 'months'}
                      </Badge>
                    </div>
                  </div>
                );
              })()}
              
              {/* Comment */}
              {(simulation.admin_comment || simulation.admin_counter_proposal?.comment) && (
                <div className="mt-2 flex items-start gap-1">
                  <MessageSquare className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic line-clamp-2">
                    {simulation.admin_comment || simulation.admin_counter_proposal?.comment}
                  </p>
                </div>
              )}
              
              {/* Accept/Refuse buttons for counter-proposal - only for commercial users */}
              {isCounter && !simulation.counter_proposal_response && isCommercial && onRespondCounterProposal && (
                <div className="mt-4 pt-3 border-t border-orange-200 dark:border-orange-800">
                  <p className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-3 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {t.responseRequired}
                  </p>
                  
                  {!showResponseForm ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                        onClick={() => {
                          setShowResponseForm('accept');
                        }}
                        data-testid="accept-counter-proposal-btn"
                      >
                        <Check className="w-3 h-3" />
                        {t.acceptCounterProposal}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-1"
                        onClick={() => {
                          setShowResponseForm('refuse');
                        }}
                        data-testid="refuse-counter-proposal-btn"
                      >
                        <X className="w-3 h-3" />
                        {t.refuseCounterProposal}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Textarea
                        placeholder={t.addComment}
                        value={responseComment}
                        onChange={(e) => setResponseComment(e.target.value)}
                        className="text-xs h-16"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className={`flex-1 gap-1 ${showResponseForm === 'accept' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'} text-white`}
                          onClick={async () => {
                            setResponding(true);
                            try {
                              await onRespondCounterProposal(showResponseForm, responseComment);
                              setShowResponseForm(false);
                              setResponseComment('');
                            } catch (e) {
                              console.error(e);
                            }
                            setResponding(false);
                          }}
                          disabled={responding}
                        >
                          {responding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          {t.confirm} {showResponseForm === 'accept' ? t.acceptCounterProposal : t.refuseCounterProposal}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowResponseForm(false);
                            setResponseComment('');
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Show response status if already responded */}
              {simulation.counter_proposal_response && (() => {
                const cprRaw = simulation.counter_proposal_response;
                const cprValue = typeof cprRaw === 'string' ? cprRaw : cprRaw?.response;
                const cprComment = typeof cprRaw === 'object' ? cprRaw?.comment : null;
                const isAccepted = cprValue === 'accepted';
                return (
                <div className={`mt-3 p-2 rounded-lg ${
                  isAccepted
                    ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <p className={`text-xs font-medium ${
                    isAccepted
                      ? 'text-emerald-700 dark:text-emerald-300' 
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    {isAccepted
                      ? (language === 'fr' ? '✓ Contre-proposition acceptée' : '✓ Counter-proposal accepted')
                      : (language === 'fr' ? '✗ Contre-proposition refusée' : '✗ Counter-proposal refused')
                    }
                  </p>
                  {cprComment && (
                    <p className="text-xs text-slate-500 mt-1 italic">
                      "{cprComment}"
                    </p>
                  )}
                </div>
                );
              })()}
              
              {/* Modifier button for direction - INLINE EDITING */}
              <div className="mt-3 pt-2 border-t border-emerald-100 dark:border-emerald-800">
                {editingDirection ? (
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      disabled={savingDirection}
                      onClick={async () => {
                        if (!onModifyDirection) {
                          // Fallback to scroll if no handler
                          const element = document.getElementById('admin-decision-section');
                          if (element) element.scrollIntoView({ behavior: 'smooth' });
                          setEditingDirection(false);
                          return;
                        }
                        setSavingDirection(true);
                        try {
                          await onModifyDirection({
                            ca_previsionnel_ht: parseFloat(editDirectionCA),
                            commission_rate: parseFloat(editDirectionCommission) / 100
                          });
                          setEditingDirection(false);
                        } catch (error) {
                          console.error('Error saving direction decision:', error);
                        } finally {
                          setSavingDirection(false);
                        }
                      }}
                      className="flex-1 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {savingDirection ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                      {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingDirection(false)}
                      className="gap-1.5 text-slate-600 border-slate-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Get the correct values (counter-proposal takes priority)
                        const hasCounterProposal = simulation.admin_counter_proposal?.commission_rate || simulation.admin_counter_proposal?.ca_previsionnel_ht;
                        const displayCA = hasCounterProposal 
                          ? simulation.admin_counter_proposal?.ca_previsionnel_ht 
                          : simulation.ca_previsionnel_ht_proposed;
                        const displayCommission = hasCounterProposal 
                          ? simulation.admin_counter_proposal?.commission_rate 
                          : simulation.commission_rate_proposed;
                        
                        setEditDirectionCA(displayCA?.toString() || '');
                        setEditDirectionCommission(((displayCommission || 0) * 100).toFixed(0));
                        setEditingDirection(true);
                      }}
                      className="flex-1 gap-1.5 text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                      data-testid="modify-direction-btn"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      {language === 'fr' ? 'Modifier' : 'Edit'}
                    </Button>
                    {/* Green "Valider Offre Définitive" button to finalize the decision */}
                    {!simulation.business_status && onValidateDirection && (
                      <Button
                        size="sm"
                        onClick={() => onValidateDirection()}
                        className="flex-1 gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid="validate-direction-btn"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {language === 'fr' ? 'Valider Offre Définitive' : 'Validate Final Offer'}
                      </Button>
                    )}
                    {/* Show validated status if already validated */}
                    {simulation.business_status === 'signed' && (
                      <Badge className="bg-emerald-500 text-white text-xs font-bold flex items-center gap-1 px-3 py-1">
                        <Check className="w-3 h-3" />
                        {language === 'fr' ? 'Offre Validée' : 'Offer Validated'}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : isPending ? (
            <div className="text-center py-2">
              <Clock className="w-6 h-6 text-amber-400 mx-auto mb-1" />
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                {t.waitingDecision}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span className="text-xs">{t.waitingValidation}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-0 shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
      <CardContent className="p-6">
        {/* Workflow Steps */}
        <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-6">
          {renderStepApplication()}
          
          {/* Arrow */}
          <div className="hidden lg:flex items-center justify-center">
            <ChevronRight className={`w-6 h-6 ${hasCommercialProposal ? 'text-purple-400' : 'text-slate-300'}`} />
          </div>
          <div className="lg:hidden flex justify-center">
            <div className={`w-0.5 h-6 ${hasCommercialProposal ? 'bg-purple-400' : 'bg-slate-300'}`} />
          </div>
          
          {renderStepCommerce()}
          
          {/* Arrow */}
          <div className="hidden lg:flex items-center justify-center">
            <ChevronRight className={`w-6 h-6 ${hasAdminDecision ? 'text-emerald-400' : 'text-slate-300'}`} />
          </div>
          <div className="lg:hidden flex justify-center">
            <div className={`w-0.5 h-6 ${hasAdminDecision ? 'bg-emerald-400' : 'bg-slate-300'}`} />
          </div>
          
          {renderStepDecision()}
        </div>
      </CardContent>
    </Card>
  );
};

export default ValidationWorkflow;
